import i18n from '@/i18n';
import React from 'react';
import { ReportGuideType } from '@/config/app/auth';
import { ReportField, ReportScene } from '@/infrastructure/reporter/datong/report';
import {
  CHARACTER_MATCH_ARRAY_OPTIONS,
  CHARACTER_MATCH_OPTIONS,
  NUMBER_MATCH_OPTIONS,
  TEXT_MATCH_OPTIONS,
} from '@/presentation/components/form/standards/types';
import FreeProductGuide, { Display, GuideType } from '@/presentation/components/free-product-guide';
import { enumTrpc } from '@/proto/generated/tpro_rmonitor_web_server_url_token';
import { MetricType, TimeType } from '@/proto/metric';
import { KeyDataType } from '@/proto/token';
import { WithTypeCast } from '@raft/utils';

export const  { ClusterStackType } = enumTrpc.rmonitor.web_server;
export type ClusterStackType = WithTypeCast<enumTrpc.rmonitor.web_server.ClusterStackType>;

export enum FieldsOperator {
  // 质量包含
  CONTAIN = 'CONTAIN',
  // 质量不包含
  NOTCONTAIN = 'NOTCONTAIN',
  // 性能包含
  MATCH = 'MATCH',
  // 性能不包含
  NOTMATCH = 'NOTMATCH',
  // 小于
  Lt = 'LT',
  // 小于等于
  Le = 'LE',
  // 大于
  Gt = 'GT',
  // 大于等于
  Ge = 'GE',
  // 等于
  Eq = 'EQ',
  Between = 'BETWEEN',
  // 质量是
  Is = 'IS',
  // 质量不是
  ISNOT = 'ISNOT',
  // 性能是
  OR = 'OR',
  // 性能不是
  NOT = 'NOT',
  // 模糊匹配
  LIKEARRAY = 'LIKEARRAY',
  // 中间匹配
  MIDMATCH = 'MIDMATCH'
}

export const CLUSTER_STACK_TYPE_OPERATOR = [
  {
    value: ClusterStackType.DEFAULT,
    label: i18n.t('出错堆栈'),
  },
  {
    value: ClusterStackType.ANR_TRACE_MAIN_STACK,
    label: i18n.t('ANR Trace的main线程堆栈'),
  },
];

export const LAUNCH_TIME_MAX_AND_FREE_MEM_OPERATOR = [
  {
    value: FieldsOperator.Lt,
    label: i18n.t('小于'),
  },
  {
    value: FieldsOperator.Gt,
    label: i18n.t('大于'),
  },
  {
    value: FieldsOperator.Between,
    label: i18n.t('范围'),
  },
];

export enum FieldValueType {
  Number,
  String,
  StringArray,
  TextInput,
}

export const fieldsOperatorOptions: {[key: string]: string} = {
  [FieldsOperator.CONTAIN]: i18n.t('匹配'),
  [FieldsOperator.NOTCONTAIN]: i18n.t('不匹配'),
  [FieldsOperator.MATCH]: i18n.t('匹配'),
  [FieldsOperator.NOTMATCH]: i18n.t('不匹配'),
  [FieldsOperator.Lt]: i18n.t('小于'),
  [FieldsOperator.Le]: i18n.t('小于等于'),
  // [FieldsOperator.NOT]: '不等于',
  [FieldsOperator.Gt]: i18n.t('大于'),
  [FieldsOperator.Ge]: i18n.t('大于等于'),
  [FieldsOperator.Eq]: i18n.t('等于'),
  [FieldsOperator.Between]: i18n.t('区间'),
  [FieldsOperator.Is]: i18n.t('是'),
  [FieldsOperator.ISNOT]: i18n.t('不是'),
  [FieldsOperator.OR]: i18n.t('是'),
  [FieldsOperator.NOT]: i18n.t('不是'),
  [FieldsOperator.LIKEARRAY]: i18n.t('模糊匹配'),
};
// 类型对应的操作项
export const fieldsOperators = {
  [FieldValueType.Number]: NUMBER_MATCH_OPTIONS,
  [FieldValueType.String]: CHARACTER_MATCH_OPTIONS,
  // [FieldsOperator.MATCH, FieldsOperator.NOTMATCH, FieldsOperator.LIKEARRAY]
  [FieldValueType.StringArray]: CHARACTER_MATCH_ARRAY_OPTIONS,
  [FieldValueType.TextInput]: TEXT_MATCH_OPTIONS,
};

export type CustomKeys = {
  key: string;
  label: string;
  type: KeyDataType
};

export const getTimeWindowOptions = (metricType: MetricType, scope: string, isFreeProduct: boolean) => {
  if (scope === 'issues') {
    return [
      {
        label: <FreeProductGuide
        guideType={GuideType.MODAL}
        display={Display.INLINE}
        reportGuideType={ReportGuideType.Modal}
        reportScene={ReportScene.TimeWindow}
        reportField={ReportField.MINUTE5}
        >
        {i18n.t('5分钟聚合')}
        </FreeProductGuide>,
        value: TimeType.MINUTE5,
        disabled: isFreeProduct,
      },
      {
        label: i18n.t('小时聚合'),
        value: TimeType.HOUR,
      },
      {
        label: i18n.t('按天聚合'),
        value: TimeType.DAY,
      },
      {
        label: <FreeProductGuide
        guideType={GuideType.MODAL}
        display={Display.INLINE}
        reportGuideType={ReportGuideType.Modal}
        reportScene={ReportScene.TimeWindow}
        reportField={ReportField.WEEK}
        >
        {i18n.t('按周聚合')}
          </FreeProductGuide>,
        value: TimeType.WEEK,
        disabled: isFreeProduct,
      },
      {
        label: <FreeProductGuide
        guideType={GuideType.MODAL}
        display={Display.INLINE}
        reportGuideType={ReportGuideType.Modal}
        reportScene={ReportScene.TimeWindow}
        reportField={ReportField.MONTH}
        >
        {i18n.t('按月聚合')}
        </FreeProductGuide>,
        value: TimeType.MONTH,
        disabled: isFreeProduct,
      },
    ];
  }
  return (metricType === MetricType.TYPE_ANR || metricType === MetricType.TYPE_FOOM || scope === 'issues') ? [
    {
      label: <FreeProductGuide
      guideType={GuideType.MODAL}
      display={Display.INLINE}
      reportGuideType={ReportGuideType.Modal}
      reportScene={ReportScene.TimeWindow}
      reportField={ReportField.MINUTE5}
      >
      {i18n.t('5分钟聚合')}
      </FreeProductGuide>,
      value: TimeType.MINUTE5,
      disabled: isFreeProduct,
    },
    {
      label: i18n.t('小时聚合'),
      value: TimeType.HOUR,
    },
    {
      label: i18n.t('按天聚合'),
      value: TimeType.DAY,
    },
  ] : [
    {
      label: i18n.t('小时聚合'),
      value: TimeType.HOUR,
    },
    {
      label: i18n.t('按天聚合'),
      value: TimeType.DAY,
    },
  ];
};

export const getTimeQuickOptions = (
  timeWindow: TimeType,
  scope: string,
  hiddenTimeWindow: boolean,
  showTime: boolean,
) => {
  const timeQuickSetting: Record<string, any> = {
    [TimeType.MINUTE5]: [
      {
        label: i18n.t('自定义'),
        value: 'custom',
      },
      {
        label: i18n.t('今天'),
        value: '今天',
      },
      {
        label: i18n.t('昨天'),
        value: '昨天',
      },
    ],
    [TimeType.HOUR]: [
      {
        label: i18n.t('自定义'),
        value: 'custom',
      },
      {
        label: i18n.t('今天'),
        value: '今天',
      },
      {
        label: i18n.t('昨天'),
        value: '昨天',
      },
    ],
    [TimeType.DAY]: scope === 'issues' ? [
      {
        label: i18n.t('自定义'),
        value: 'custom',
      },
      {
        label: i18n.t('近1小时'),
        value: '近1小时',
      },
      {
        label: i18n.t('今天'),
        value: '今天',
      },
      {
        label: i18n.t('昨天'),
        value: '昨天',
      },
      {
        label: i18n.t('近7天'),
        value: '近7天',
      },
      {
        label: i18n.t('近30天'),
        value: '近30天',
      },
      {
        label: i18n.t('近90天'),
        value: '近90天',
      },
    ] : [
      {
        label: i18n.t('自定义'),
        value: 'custom',
      },
      {
        label: i18n.t('近7天'),
        value: '近7天',
      },
      {
        label: i18n.t('近30天'),
        value: '近30天',
      },
      {
        label: i18n.t('近90天'),
        value: '近90天',
      },
      {
        label: i18n.t('近180天'),
        value: '近180天',
      },
    ],
  };
  if (hiddenTimeWindow) {
    const timeQuickHour = '近1小时';
    return showTime
      ? timeQuickSetting[TimeType.DAY]
      : timeQuickSetting[TimeType.DAY]?.filter((item: any) => item.value !== timeQuickHour);
  }
  return timeQuickSetting[timeWindow] ?? timeQuickSetting[TimeType.MINUTE5];
};


export const timeQuickSetting: Record<string, any> = {
  [TimeType.MINUTE5]: [
    {
      label: i18n.t('自定义'),
      value: 'custom',
    },
    {
      label: i18n.t('近1小时'),
      value: '近1小时',
    },
    {
      label: i18n.t('今天'),
      value: '今天',
    },
    {
      label: i18n.t('昨天'),
      value: '昨天',
    },
  ],
  [TimeType.HOUR]: [
    {
      label: i18n.t('自定义'),
      value: 'custom',
    },
    {
      label: i18n.t('近1小时'),
      value: '近1小时',
    },
    {
      label: i18n.t('今天'),
      value: '今天',
    },
    {
      label: i18n.t('昨天'),
      value: '昨天',
    },
  ],
  [TimeType.DAY]: [
    {
      label: i18n.t('自定义'),
      value: 'custom',
    },
    {
      label: i18n.t('近7天'),
      value: '近7天',
    },
    {
      label: i18n.t('近30天'),
      value: '近30天',
    },
    {
      label: i18n.t('近90天'),
      value: '近90天',
    },
  ],
  [TimeType.WEEK]: [
    {
      label: i18n.t('自定义'),
      value: 'custom',
    },
    {
      label: i18n.t('近7天'),
      value: '近7天',
    },
    {
      label: i18n.t('近30天'),
      value: '近30天',
    },
    {
      label: i18n.t('近90天'),
      value: '近90天',
    },
  ],
  [TimeType.MONTH]: [
    {
      label: i18n.t('自定义'),
      value: 'custom',
    },
    {
      label: i18n.t('近7天'),
      value: '近7天',
    },
    {
      label: i18n.t('近30天'),
      value: '近30天',
    },
    {
      label: i18n.t('近90天'),
      value: '近90天',
    },
  ],
  [TimeType.NULL]: [],
  [TimeType.MINUTE]: [],
};

export const getIssueTimeQuickOptions = (timeWindow: TimeType) => timeQuickSetting[timeWindow];

// 质量的默认还是用上报时间
export const TIME_TYPE_OPTIONS_BUGLY = [
  {
    label: i18n.t('上报时间'),
    value: 'report',
  },
  {
    label: i18n.t('发生时间'),
    value: 'occur',
  },
];


// 其他的默认发生时间
export const TIME_TYPE_OPTIONS = [
  {
    label: i18n.t('上报时间'),
    value: 'report',
  },
  {
    label: i18n.t('发生时间'),
    value: 'occur',
  },
];

export const DEFAULT_SLICE_ITEM_NUMBER = 0;

export enum CaseTimeType{
  report='report',
  occur='occur'
}

// 免费时间选项
export const  FREE_DATE_QUICK = ['custom', '近7天', '今天', '昨天', '近1小时'];

// 免费时间可选范围
export const FREE_DATE_RANGE = 7;
